import { Fragment, type ReactNode } from "react";

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { useIgnoreScrollbarClose } from "@vega-core/hooks/useIgnoreScrollbarClose";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  ModalButtonsGroup,
  type ModalButtonsGroupProps,
} from "../ModalButtonsGroup";
import { ThemedModalCloseButton } from "../ThemedModalCloseButton/ThemedModalCloseButton";

export type ActionaryModalProps = {
  children?: ReactNode;
  open: boolean;
  setOpen: (value: boolean) => void;
  title?: ReactNode | string;
  description?: ReactNode | string;
  fullScreen?: boolean;
  allowFullScreenClose?: boolean;
  skipCloseGuards?: boolean;
  ModalButtonsGroupProps?: ModalButtonsGroupProps;
  hideCloseButton?: boolean;
  dataTestId?: string;
  fluidWidth?: boolean;
  fluidHeight?: boolean;
  defaultWidth?: boolean;
};

//todo rebase on Modal.tsx
export function ActionaryModal({
  children,
  open,
  setOpen,
  title,
  fullScreen,
  allowFullScreenClose = false,
  skipCloseGuards = false,
  description,
  ModalButtonsGroupProps,
  hideCloseButton = false,
  dataTestId = "actionary-modal",
  fluidWidth = false,
  fluidHeight = false,
  defaultWidth = false,
}: ActionaryModalProps) {
  const { theme } = useTheme();

  const handleClose = () => {
    if (skipCloseGuards) {
      if (!fullScreen || allowFullScreenClose) {
        setOpen(false);
      }

      return;
    }

    // Prevent modal from closing when dropdowns/selects are open
    // This fixes the issue where clicking on modal content to close a dropdown
    // would also close the modal itself due to HeadlessUI Dialog's outside-click behavior

    // Look for Radix UI Select components that are open (our ThemedDropdown uses Radix)
    const openSelects = document.querySelectorAll(
      "[data-radix-select-content]",
    );

    // Look for any open popovers or dropdowns with data-state="open"
    const openPopovers = document.querySelectorAll('[data-state="open"]');

    // If there are any open dropdowns/selects/popovers, don't close the modal
    // This allows users to click on modal content to close dropdowns without closing the modal
    if (openSelects.length > 0 || openPopovers.length > 0) {
      return;
    }

    if (!fullScreen || allowFullScreenClose) {
      setOpen(false);
    }
  };

  const handleScrollbarAwareClose = useIgnoreScrollbarClose({
    open,
    onClose: handleClose,
  });

  return (
    <Transition as={Fragment} show={open}>
      <Dialog
        as="div"
        className={cnTwMerge("relative z-modal text-text/primary", theme)}
        onClose={handleScrollbarAwareClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 overlay transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-modal overflow-y-auto h-auto">
          <div
            className={cnTwMerge(
              "flex items-center justify-center min-h-full p-4 sm:p-6 text-center",
              {
                "mx-auto max-w-2xl": !fullScreen && !fluidWidth,
                "p-0 sm:p-0 mx-0": fullScreen,
              },
            )}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={cnTwMerge(
                  "relative overflow-visible transform bg-background/card rounded-containerCorner border-border/secondary shadow-popover max-w-[100vw]",
                  "p-4 sm:p-6 md:p-8",
                  {
                    "[@media(max-height:868px)]:h-[60dvh] [@media(max-height:868px)]:flex [@media(max-height:868px)]:flex-col [@media(max-height:868px)]:overflow-auto":
                      !fullScreen && !fluidHeight,
                  },
                  {
                    "fixed inset-0 h-screen w-screen rounded-none": fullScreen,
                  },
                  { "p-0 sm:p-0 md:p-0": fullScreen },
                  {
                    "w-min max-w-none min-w-0": fluidWidth,
                    "h-min! min-h-min!": fluidHeight,
                    "w-actionary-modal": defaultWidth,
                  },
                )}
                data-testid={dataTestId}
              >
                {isTruthy(title) ? (
                  <DialogTitle
                    as="h3"
                    className="text-start w-full flex flex-col space-y-2 pr-8"
                  >
                    <span className="heading-5 sm:heading-4 text-text/primary">
                      {title}
                    </span>
                    {isTruthy(description) && (
                      <span className="body-base-regular text-text/secondary">
                        {description}
                      </span>
                    )}
                  </DialogTitle>
                ) : null}
                {isFalsy(hideCloseButton) && (
                  <ThemedModalCloseButton onClose={() => setOpen(false)} />
                )}
                <div
                  className={cnTwMerge(
                    "mt-4 sm:mt-6 [@media(max-height:868px)]:flex-1",
                    {
                      "mt-0 sm:mt-0": fullScreen,
                    },
                  )}
                >
                  <div
                    className={cnTwMerge(
                      "[@media(max-height:868px)]:h-full [@media(max-height:868px)]:flex [@media(max-height:868px)]:flex-col",
                      {
                        "w-min": fluidWidth,
                        "h-min": fluidHeight,
                      },
                    )}
                  >
                    {children}
                  </div>
                </div>
                {isTruthy(ModalButtonsGroupProps) ? (
                  <div className="mt-6 sm:mt-8">
                    <ModalButtonsGroup {...ModalButtonsGroupProps} />
                  </div>
                ) : null}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
